import type { RisqPrepTiming } from './content';
import { compareRisqCodes } from './codes';
import { isDone, type RisqStatus } from './status';

/**
 * "What to do now" from the owner's preparation flags (master file L–O, PLAN §2.10) and the
 * planned inspection date of the vessel (§2.11). Dates are calendar dates `YYYY-MM-DD`
 * (the phone's local date; the inspection date has no time).
 */

/** Preparation phase of a vessel: which flag's time has come. */
export const PREP_PHASES = [
  'early',
  'six_months',
  'three_months',
  'last',
  'final_24h',
  'passed',
] as const;
export type PrepPhase = (typeof PREP_PHASES)[number];

/** Flags in the order their time comes. */
export const PREP_TIMING_ORDER: readonly RisqPrepTiming[] = [
  'six_months',
  'three_months',
  'last',
  'final_24h',
];

/**
 * "Last" = the vessel's last voyage before the inspection (owner, Q26): it starts on the day the
 * Master records as the start of the last voyage. Until then, 7 days before the inspection.
 */
export const LAST_DAYS = 7;

/**
 * Questions without a timing flag (174 of 371 in RISQ 3.2) are treated as 3-m (owner, Q27).
 * The flag can be set per company in the content editor.
 */
export const DEFAULT_TIMING: RisqPrepTiming = 'three_months';

/** Timing flags of a question with the default applied: no flag = 3-m. */
export function effectiveTimings(timings: readonly RisqPrepTiming[]): readonly RisqPrepTiming[] {
  return timings.length === 0 ? [DEFAULT_TIMING] : timings;
}

/** The planned inspection of a vessel (dates `YYYY-MM-DD`). */
export interface InspectionPlan {
  plannedOn: string;
  /** Day the last voyage before the inspection started; null = not recorded yet. */
  lastVoyageStartedOn?: string | null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDate(value: string): Date {
  const match = ISO_DATE.exec(value);
  if (!match) {
    throw new RangeError(`Expected a date YYYY-MM-DD, got ${value}`);
  }
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (date.toISOString().slice(0, 10) !== value) {
    throw new RangeError(`Not a calendar date: ${value}`);
  }
  return date;
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function isIsoDate(value: string): boolean {
  try {
    parseDate(value);
    return true;
  } catch {
    return false;
  }
}

/** Calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseDate(to).getTime() - parseDate(from).getTime()) / 86_400_000);
}

export function addDays(date: string, days: number): string {
  const result = parseDate(date);
  result.setUTCDate(result.getUTCDate() + days);
  return formatDate(result);
}

/** Adds calendar months; the day is clamped to the month's length (31 Aug − 6 months = 28/29 Feb). */
export function addMonths(date: string, months: number): string {
  const start = parseDate(date);
  const target = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(start.getUTCDate(), lastDay));
  return formatDate(target);
}

/** The local calendar date of a moment, e.g. today on the phone. */
export function localDate(moment: Date): string {
  const y = moment.getFullYear();
  const m = String(moment.getMonth() + 1).padStart(2, '0');
  const d = String(moment.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The day each flag's work starts. "Last" starts with the last voyage when it is recorded
 * (never later than the C/L day), else 7 days before the inspection.
 */
export function prepStartDates(plan: InspectionPlan): Record<RisqPrepTiming, string> {
  const final24h = addDays(plan.plannedOn, -1);
  const voyage = plan.lastVoyageStartedOn ?? null;
  return {
    six_months: addMonths(plan.plannedOn, -6),
    three_months: addMonths(plan.plannedOn, -3),
    last:
      voyage === null
        ? addDays(plan.plannedOn, -LAST_DAYS)
        : daysBetween(voyage, final24h) < 0
          ? final24h
          : voyage,
    final_24h: final24h,
  };
}

export function prepPhase(today: string, plan: InspectionPlan): PrepPhase {
  if (daysBetween(today, plan.plannedOn) < 0) {
    return 'passed';
  }
  const starts = prepStartDates(plan);
  for (const timing of [...PREP_TIMING_ORDER].reverse()) {
    if (daysBetween(starts[timing], today) >= 0) {
      return timing;
    }
  }
  return 'early';
}

/** Flags whose time has come in a phase. */
export function dueTimings(phase: PrepPhase): readonly RisqPrepTiming[] {
  if (phase === 'early') {
    return [];
  }
  if (phase === 'passed') {
    return PREP_TIMING_ORDER;
  }
  return PREP_TIMING_ORDER.slice(0, PREP_TIMING_ORDER.indexOf(phase) + 1);
}

/**
 * When a question's work is due: `now` — a flag of the current phase; `started` — only flags whose
 * time came earlier (catch up); `later` — all flags still ahead. No timing flag counts as 3-m.
 */
export type PrepDue = 'now' | 'started' | 'later';

export function prepDue(flags: readonly RisqPrepTiming[], phase: PrepPhase): PrepDue {
  const timings = effectiveTimings(flags);
  if (
    (PREP_TIMING_ORDER as readonly string[]).includes(phase) &&
    timings.includes(phase as RisqPrepTiming)
  ) {
    return 'now';
  }
  const due = dueTimings(phase);
  return timings.some((t) => due.includes(t)) ? 'started' : 'later';
}

export interface PrepItem {
  code: string;
  status: RisqStatus;
  prepTiming: readonly RisqPrepTiming[];
}

function earliestTiming(timings: readonly RisqPrepTiming[]): number {
  return Math.min(...effectiveTimings(timings).map((t) => PREP_TIMING_ORDER.indexOf(t)));
}

/**
 * Open questions whose time has come, most overdue first (earliest flag), then by code.
 * Done questions (Closed, Office action) are left out; no timing flag counts as 3-m.
 */
export function whatToDoNow<T extends PrepItem>(items: readonly T[], phase: PrepPhase): T[] {
  return items
    .filter((item) => !isDone(item.status))
    .filter((item) => {
      const due = prepDue(item.prepTiming, phase);
      return due === 'now' || due === 'started';
    })
    .sort(
      (a, b) =>
        earliestTiming(a.prepTiming) - earliestTiming(b.prepTiming) ||
        compareRisqCodes(a.code, b.code),
    );
}

export interface PrepSummary {
  phase: PrepPhase;
  daysLeft: number;
  starts: Record<RisqPrepTiming, string>;
  /** "Last" starts with the recorded last voyage (true) or 7 days before (false). */
  lastFromVoyage: boolean;
  /** Start of the next phase, or null when the last one has begun. */
  next: { timing: RisqPrepTiming; date: string } | null;
}

export function prepSummary(today: string, plan: InspectionPlan): PrepSummary {
  const phase = prepPhase(today, plan);
  const starts = prepStartDates(plan);
  const upcoming = PREP_TIMING_ORDER.find((t) => daysBetween(today, starts[t]) > 0);
  return {
    phase,
    daysLeft: daysBetween(today, plan.plannedOn),
    starts,
    lastFromVoyage: (plan.lastVoyageStartedOn ?? null) !== null,
    next: upcoming === undefined ? null : { timing: upcoming, date: starts[upcoming] },
  };
}

/**
 * Whether the Master can still record the start of the last voyage: an inspection is planned,
 * the voyage is not recorded yet and the inspection has not passed.
 */
export function canRecordLastVoyage(today: string, plan: InspectionPlan | null): boolean {
  return (
    plan !== null &&
    (plan.lastVoyageStartedOn ?? null) === null &&
    daysBetween(today, plan.plannedOn) >= 0
  );
}

/** Earliest and latest day the last voyage may have started (same rule as the server). */
export function lastVoyageRange(today: string, plannedOn: string): { from: string; to: string } {
  const tomorrow = addDays(today, 1);
  return {
    from: addMonths(plannedOn, -6),
    to: daysBetween(tomorrow, plannedOn) < 0 ? plannedOn : tomorrow,
  };
}
